import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import { useTheme } from "@mui/material/styles";
import React from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { selectBtnStyle, selectMenuListStyle, selectMenuPaperStyle, selectMenuStyle } from "./stylesheet";

import SwitcherMenuItem from "./SwitcherMenuItem";
import { useOrganization, useOrganizations } from "../../../hooks";
import useGlobalAlert from "../../../hooks/useGlobalAlert";
import { ExtendedErrorResponse } from "../../../types/ErrorResponse";
import { Organization } from "../../../types/Organization";

export default function OrganizationSwitcher(): JSX.Element {
    const { t } = useTranslation();
    const alerts = useGlobalAlert();
    const navigate = useNavigate();
    const theme = useTheme();
    const { organizationId } = useParams();
    const [anchorElSelect, setAnchorElSelect] = React.useState<null | HTMLElement>(null);
    const { data: organizations, error } = useOrganizations();
    const { data: selectedOrg } = useOrganization(organizationId || "autodesk");

    const handleMenuClicked = (selectedName: string) => {
        setAnchorElSelect(null);

        if (selectedName == "autodesk") {
            navigate("/home");
        } else {
            navigate(`/organization/${selectedName}`);
        }
    };

    const handleOpenSelector = (event: React.MouseEvent<HTMLElement>) => {
        if (anchorElSelect) {
            handleCloseSelector();
        } else {
            setAnchorElSelect(event.currentTarget);
        }
    };

    const handleCloseSelector = () => {
        setAnchorElSelect(null);
    };

    function renderMenu(): JSX.Element {
        return (
            <Menu
                sx={selectMenuStyle}
                id="menu-appbar-profile"
                anchorEl={anchorElSelect}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                open={Boolean(anchorElSelect)}
                onClose={handleCloseSelector}
                PaperProps={selectMenuPaperStyle}
                MenuListProps={selectMenuListStyle}
                autoFocus={false}
            >
                {organizations?.map((org: Organization) => (
                    <SwitcherMenuItem
                        key={org.name}
                        organization={org}
                        selected={selectedOrg}
                        handleMenuClicked={handleMenuClicked}
                    />
                ))}
            </Menu>
        );
    }

    React.useEffect(() => {
        if (error) {
            const err = error as ExtendedErrorResponse;
            if (err.status === 431) {
                // 431 Request Header Fields Too Large
                alerts.error(t("alerts.requestHeaderFieldsTooLarge"));
            } else {
                alerts.error(`${err.status} ${err.statusText}`);
            }
        }
    }, [error]);

    return (
        <Button
            variant="text"
            onClick={handleOpenSelector}
            sx={selectBtnStyle}
            data-testid="organization-switcher"
            endIcon={
                anchorElSelect ? (
                    <KeyboardArrowUpIcon sx={{ color: theme.palette.common.white }} />
                ) : (
                    <KeyboardArrowDownIcon sx={{ color: theme.palette.common.white }} />
                )
            }
        >
            Organizations
            {renderMenu()}
        </Button>
    );
}
